import { apiClient } from './apiClient';

export interface SchemaDraftRequest {
  name: string;
  alias: string | null;
  isEnabled: boolean;
  inputSchemaJson: string;
  mappingJson: string;
  outputVersion: number;
  expectedRevision: number;
}

export interface SchemaDraft extends Omit<SchemaDraftRequest, 'expectedRevision'> {
  schemaId: string;
  revision: number;
  activeVersion: number | null;
  latestPublishedVersion?: number | null;
  hasPublishedHistory?: boolean;
  status: string;
}

export interface SchemaSettingsRequest {
  outputSchemaJson: string;
  outputVersion: number;
  runtimeBindingsJson: string;
  expectedRevision: number;
  mappingUpdates?: { schemaId: string; expectedRevision: number; mappingJson: string }[];
}

export interface SchemaSettings {
  payloadMode?: 'Direct' | 'Normalise';
  directSchemaId?: string | null;
  directVersion?: number | null;
  directRuntimeBindingsJson?: string;
  effectiveOutputSchemaJson?: string | null;
  effectiveRuntimeBindingsJson?: string | null;
  revision: number;
  strictEnabled: boolean;
  mode: string;
  draftOutputSchemaJson: string;
  draftOutputVersion: number;
  draftRuntimeBindingsJson: string;
  activeOutputSchemaJson: string | null;
  activeOutputVersion: number | null;
  activeRuntimeBindingsJson: string | null;
}

export interface SchemaRegistry {
  definitions: SchemaDraft[];
  settings: SchemaSettings;
}

export interface SchemaMutation {
  schemaId: string | null;
  revision: number;
  version: number | null;
  status: string;
  activationChanged: boolean;
}

export interface SchemaActivationPreview {
  revision: number;
  compatible: boolean;
  items: { schemaId: string; name: string; activeVersion: number | null; requestedVersion: number;
    latestPublishedVersion: number | null; compatible: boolean; error: string | null }[];
  error: string | null;
}

export interface SchemaSnapshot extends Omit<SchemaDraft, 'revision' | 'activeVersion'> {
  version: number;
  activationReadinessAssessed: boolean;
}

export interface SchemaPreview {
  outcome: string;
  schemaId: string | null;
  candidates: string[];
  normalisedPayload: string | null;
  error: string | null;
  selectionScope: string;
  outputSource: string;
  outputVersion: number;
  validationOnly: boolean;
  mappingEvaluated: boolean;
  activationReadinessAssessed: boolean;
  runtimeBindings?: { evaluated: boolean; valid: boolean; error: string | null;
    projection: { title: string; severity: string; status: string; sourceEventId: string; occurredUtc: string | null; nonAlert: boolean } | null } | null;
}

export interface SchemaLegacyMappingPreview {
  canTranslate: boolean;
  proposedMappingJson: string | null;
  manualReviewReasons: string[];
  outputSettingsAvailable: boolean;
  outputVersion: number | null;
  outputCompatible: boolean | null;
  outputCompatibilityError: string | null;
}

export interface SchemaMigrationInventory {
  totalIntegrations: number;
  alertIntegrations: number;
  notificationIntegrations: number;
  enabledIntegrations: number;
  disabledIntegrations: number;
  legacyDefaultTransform: number;
  legacyCustomTransform: number;
  missingTransformType: number;
  hasRegistrySettings: number;
  strictEnabled: number;
  activeOutputConfigured: number;
  draftOutputConfigured: number;
}

export interface SchemaMigrationPreview {
  legacyMode: string;
  legacyJsonataExecuted: boolean;
  legacyOutput: {
    title: string | null;
    alertName: string | null;
    description: string | null;
    service: string | null;
    source: string | null;
    severity: string | null;
    status: string | null;
    externalAlertId: string | null;
    correlationId: string | null;
    occurredAt: string | null;
    isResolved: boolean;
  };
  registeredOutcome: string;
  registeredOutputJson: string | null;
  registeredError: string | null;
  registeredRuntime: { title: string; severity: string; status: string; sourceEventId: string; occurredUtc: string | null; nonAlert: boolean } | null;
  runtimeError: string | null;
  notes: string[];
}

const registry = (integrationId: string) => `/api/inbound-integrations/${encodeURIComponent(integrationId)}/schemas`;
const definitionPath = (integrationId: string, schemaId: string) => `${registry(integrationId)}/${encodeURIComponent(schemaId)}`;

export interface SchemaReceiptSummary {
  receiptId: string;
  state: string;
  eventType: string;
  receivedAt: string;
  schemaId: string | null;
  schemaVersion: number | null;
  outputVersion: number | null;
  error: string | null;
  dispositionId?: string | null;
  isDismissed?: boolean;
  actorId?: string | null;
  reason?: string | null;
  recordedAt?: string | null;
}

export interface SchemaReceiptPage {
  items: SchemaReceiptSummary[];
  limit: number;
  total: number;
  nextCursor: string | null;
}
export interface SchemaDispositionHistory {
  dispositionId: string; receiptId: string; requestId: string; isDismissed: boolean;
  actorId: string; reason: string | null; recordedAt: string;
}

export interface SchemaReceiptDetail {
  receipt: SchemaReceiptSummary;
  eventId: string;
  registryRevision: number;
  rawPayload: string;
  normalisedPayload: string | null;
  outputSchemaJson: string | null;
  runtimeBindingsJson: string | null;
  dispatchStatus?: string | null;
  dispatchedAt?: string | null;
}

export interface SchemaReassessment {
  registryRevision: number;
  assessedAt: string;
  selectionScope: 'ActiveRegistry' | 'DisabledDraft';
  liveRouting: 'Strict' | 'LegacyStrictOff' | 'DirectStrictOff';
  validationOnly: boolean;
  items: { receiptId: string; originalState: string | null; outcome: string; structuralOutcome: string;
    validationOutcome: string; candidates: { schemaId: string; name: string; version: number | null;
      draftRevision: number | null; isEnabled: boolean }[]; error: string | null }[];
}

export interface SchemaReleaseRequest {
  requestId: string;
  schemaId: string;
  schemaVersion: number;
  expectedRegistryRevision: number;
  previewDigest?: string;
}
export interface SchemaReleasePreview {
  receiptId: string;
  schemaId: string;
  schemaVersion: number;
  registryRevision: number;
  outputVersion: number;
  normalisedPayload: string;
  outputSchemaJson: string;
  runtimeBindingsJson: string;
  previewDigest: string;
  validationOnly: boolean;
  eventType?: 'Notification' | 'Alert';
  alertTarget?: { sourceEventId: string; fingerprint: string; intent: string;
    outcome: 'CreatesNew' | 'UpdatesExisting' | 'ResolvesExisting';
    alertId: string | null; status: string | null; updatedAt: string | null } | null;
}
export interface SchemaReleaseResult {
  releaseId: string;
  parentReceiptId: string;
  childReceiptId: string;
  schemaId: string;
  schemaVersion: number;
  registryRevision: number;
  state: string;
}

export const inboundSchemasApi = {
  getReceiptPage: (integrationId: string, status: string, disposition: string, cursor: string | null = null, limit = 50) => {
    const query = new URLSearchParams({ status, disposition, limit: String(limit) });
    if (cursor) query.set('cursor', cursor);
    return apiClient.get<SchemaReceiptPage>(`${registry(integrationId)}/receipts/page?${query}`);
  },
  changeReceiptDisposition: (integrationId: string, requestId: string, isDismissed: boolean,
    items: { receiptId: string; expectedDispositionId: string | null }[], reason: string | null) =>
    apiClient.post<{ receiptId: string; dispositionId: string | null; success: boolean; error: string | null }[]>(
      `${registry(integrationId)}/receipts/disposition`, { requestId, isDismissed, items, reason }),
  getReceiptDispositionHistory: (integrationId: string, receiptId: string) =>
    apiClient.get<SchemaDispositionHistory[]>(`${registry(integrationId)}/receipts/${encodeURIComponent(receiptId)}/dispositions`),
  reassessReceipts: (integrationId: string, receiptIds: string[], expectedRegistryRevision: number,
    schemaId?: string, expectedSchemaRevision?: number) =>
    apiClient.post<SchemaReassessment>(`${registry(integrationId)}/receipts/reassess`, {
      receiptIds, expectedRegistryRevision, schemaId, expectedSchemaRevision }),
  saveMode: (integrationId: string, payloadMode: 'Direct' | 'Normalise', expectedRevision: number,
    schemaId?: string, expectedSchemaRevision?: number, directRuntimeBindingsJson?: string) =>
    apiClient.put<SchemaRegistry>(`${registry(integrationId)}/mode`, { payloadMode, expectedRevision,
      schemaId, expectedSchemaRevision, directRuntimeBindingsJson }),
  getMigrationInventory: () =>
    apiClient.get<SchemaMigrationInventory>('/api/inbound-integrations/schemas/migration-inventory'),
  previewMigration: (integrationId: string, schemaId: string, payload: unknown) =>
    apiClient.post<SchemaMigrationPreview>(`${registry(integrationId)}/migration-preview`, { schemaId, payload }),
  list: (integrationId: string) => apiClient.get<{ definitions: SchemaDraft[]; settings: SchemaSettings }>(registry(integrationId)),
  previewLegacyMapping: (integrationId: string) =>
    apiClient.get<SchemaLegacyMappingPreview>(`${registry(integrationId)}/legacy-preview`),
  initialise: (integrationId: string) => apiClient.post<SchemaMutation>(`${registry(integrationId)}/initialise`),
  activate: (integrationId: string, expectedRevision: number, schemaVersions: Record<string, number>, strictEnabled = true) =>
    apiClient.post<SchemaMutation>(`${registry(integrationId)}/activate`, { expectedRevision, schemaVersions, strictEnabled }),
  previewActivation: (integrationId: string, expectedRevision: number, schemaVersions: Record<string, number>) =>
    apiClient.post<SchemaActivationPreview>(`${registry(integrationId)}/activation-preview`, { expectedRevision, schemaVersions }),
  deactivate: (integrationId: string, expectedRevision: number) =>
    apiClient.post<SchemaMutation>(`${registry(integrationId)}/deactivate`, { expectedRevision }),
  previewRelease: (integrationId: string, receiptId: string, request: SchemaReleaseRequest) =>
    apiClient.post<SchemaReleasePreview>(`${registry(integrationId)}/receipts/${encodeURIComponent(receiptId)}/release-preview`, request),
  releaseReceipt: (integrationId: string, receiptId: string, request: SchemaReleaseRequest) =>
    apiClient.post<SchemaReleaseResult>(`${registry(integrationId)}/receipts/${encodeURIComponent(receiptId)}/release`, request),
  listReleases: (integrationId: string, receiptId: string) =>
    apiClient.get<SchemaReleaseResult[]>(`${registry(integrationId)}/receipts/${encodeURIComponent(receiptId)}/releases`),
  listReceipts: (integrationId: string, limit = 100) => {
    if (!Number.isInteger(limit) || limit < 1 || limit > 100) throw new Error('Receipt limit must be an integer from 1 to 100.');
    return apiClient.get<{ items: SchemaReceiptSummary[]; limit: number }>(`${registry(integrationId)}/receipts?limit=${limit}`);
  },
  getReceipt: (integrationId: string, receiptId: string) => apiClient.get<SchemaReceiptDetail>(`${registry(integrationId)}/receipts/${encodeURIComponent(receiptId)}`),
  getDraft: (integrationId: string, schemaId: string) => apiClient.get<SchemaDraft>(`${definitionPath(integrationId, schemaId)}/draft`),
  deleteDraft: (integrationId: string, schemaId: string, expectedRevision: number, expectedRegistryRevision: number) =>
    apiClient.delete<SchemaMutation>(`${definitionPath(integrationId, schemaId)}/draft?expectedRevision=${expectedRevision}&expectedRegistryRevision=${expectedRegistryRevision}`),
  getVersion: (integrationId: string, schemaId: string, version: number) => apiClient.get<SchemaSnapshot>(`${definitionPath(integrationId, schemaId)}/versions/${version}`),
  saveDraft: (integrationId: string, schemaId: string, request: SchemaDraftRequest) => apiClient.put<SchemaMutation>(`${definitionPath(integrationId, schemaId)}/draft`, request),
  saveSettings: (integrationId: string, request: SchemaSettingsRequest) => apiClient.put<SchemaMutation>(`${registry(integrationId)}/settings`, request),
  savePublished: (integrationId: string, schemaId: string, draft: SchemaDraftRequest, expectedSettingsRevision: number,
    directRuntimeBindingsJson?: string, payloadMode?: 'Direct' | 'Normalise') =>
    apiClient.put<SchemaRegistry>(`${definitionPath(integrationId, schemaId)}/save`, { draft, expectedSettingsRevision,
      ...(directRuntimeBindingsJson === undefined ? {} : { directRuntimeBindingsJson }), ...(payloadMode === undefined ? {} : { payloadMode }) }),
  saveOutput: (integrationId: string, request: SchemaSettingsRequest) =>
    apiClient.put<SchemaRegistry>(`${registry(integrationId)}/settings/save`, request),
  publish: (integrationId: string, schemaId: string, expectedRevision: number, expectedSettingsRevision: number) =>
    apiClient.post<SchemaMutation>(`${definitionPath(integrationId, schemaId)}/publish`, { expectedRevision, expectedSettingsRevision }),
  preview: (integrationId: string, definition: SchemaDraftRequest, payload: unknown, runtimeBindingsJson?: string, payloadMode?: 'Direct' | 'Normalise') =>
    apiClient.post<SchemaPreview>(`${registry(integrationId)}/preview`, { definition, payload,
      ...(runtimeBindingsJson === undefined ? {} : { runtimeBindingsJson }), ...(payloadMode === undefined ? {} : { payloadMode }) }),
};

type JsonSchema = Record<string, unknown>;
const MAX_NODES = 2000;
const MAX_DEPTH = 24;

export function parseBoundedJson(text: string): unknown {
  if (new TextEncoder().encode(text).length > 512 * 1024) throw new Error('JSON exceeds the editor limit of 512 KiB.');
  const value: unknown = JSON.parse(text);
  let nodes = 0;
  const visit = (node: unknown, depth: number) => {
    if (++nodes > MAX_NODES || depth > MAX_DEPTH) throw new Error('JSON exceeds the editor depth or node limit.');
    if (node !== null && typeof node === 'object') Object.values(node).forEach(child => visit(child, depth + 1));
  };
  visit(value, 0);
  return value;
}

export function parseJsonObject(text: string): Record<string, unknown> {
  const value = parseBoundedJson(text);
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('JSON configuration must be an object.');
  return value as Record<string, unknown>;
}

export function inferInputSchema(text: string): JsonSchema {
  const value = parseBoundedJson(text);
  const infer = (examples: unknown[]): JsonSchema => {
    const groups = new Map<string, unknown[]>();
    examples.forEach(example => {
      const type = example === null ? 'null' : Array.isArray(example) ? 'array' : typeof example === 'number'
        ? Number.isInteger(example) ? 'integer' : 'number' : typeof example;
      groups.set(type, [...(groups.get(type) ?? []), example]);
    });
    if (groups.has('number') && groups.has('integer')) {
      groups.set('number', [...groups.get('number')!, ...groups.get('integer')!]);
      groups.delete('integer');
    }
    const alternatives = Array.from(groups, ([type, instances]): JsonSchema => {
      if (type === 'object') {
        const objects = instances as Record<string, unknown>[];
        const keys = Array.from(new Set(objects.flatMap(Object.keys))).sort();
        return { type, properties: Object.fromEntries(keys.map(key => [key, infer(objects.filter(object => Object.prototype.hasOwnProperty.call(object, key)).map(object => object[key]))])),
          required: keys.filter(key => objects.every(object => Object.prototype.hasOwnProperty.call(object, key))) };
      }
      if (type === 'array') {
        const items = (instances as unknown[][]).flat();
        return { type, items: items.length ? infer(items) : {} };
      }
      return { type };
    });
    return alternatives.length === 1 ? alternatives[0] : { anyOf: alternatives };
  };
  return { $schema: 'https://json-schema.org/draft/2020-12/schema', ...infer([value]) };
}

const pointerSegment = (key: string) => key.replace(/~/g, '~0').replace(/\//g, '~1');

export function sourcePointers(schemaText: string, exampleText: string): string[] {
  const paths = new Set<string>(['']);
  let nodes = 0;
  const add = (path: string) => { if (paths.size < 200) paths.add(path); };
  try {
    const schema = parseJsonObject(schemaText);
    const visit = (node: unknown, path: string, depth: number) => {
      if (++nodes > MAX_NODES || depth > MAX_DEPTH || !node || typeof node !== 'object') return;
      const current = node as JsonSchema;
      if (typeof current.$ref === 'string' && current.$ref.startsWith('#/')) {
        const referenced = current.$ref.slice(2).split('/').reduce<unknown>((parent, part) => parent && typeof parent === 'object'
          ? (parent as JsonSchema)[part.replace(/~1/g, '/').replace(/~0/g, '~')] : undefined, schema);
        visit(referenced, path, depth + 1);
      }
      if (current.properties && typeof current.properties === 'object') Object.entries(current.properties).forEach(([key, child]) => {
        const next = `${path}/${pointerSegment(key)}`; add(next); visit(child, next, depth + 1);
      });
      if (current.items) { add(`${path}/0`); visit(current.items, `${path}/0`, depth + 1); }
      if (Array.isArray(current.prefixItems)) current.prefixItems.forEach((child, index) => {
        add(`${path}/${index}`); visit(child, `${path}/${index}`, depth + 1);
      });
      for (const keyword of ['anyOf', 'oneOf', 'allOf']) if (Array.isArray(current[keyword]))
        (current[keyword] as unknown[]).forEach(child => visit(child, path, depth + 1));
    };
    visit(schema, '', 0);
  } catch { /* Suggestions are optional while JSON is being edited. */ }
  try {
    const visit = (node: unknown, path: string) => {
      if (++nodes > MAX_NODES || !node || typeof node !== 'object') return;
      Object.entries(node).forEach(([key, child]) => { const next = `${path}/${pointerSegment(key)}`; add(next); visit(child, next); });
    };
    if (exampleText.trim()) visit(parseBoundedJson(exampleText), '');
  } catch { /* Invalid samples do not prevent schema-only editing. */ }
  return Array.from(paths);
}

export const effectiveOutputSchema = (settings: SchemaSettings | null | undefined) => settings?.payloadMode === 'Direct'
  ? settings.effectiveOutputSchemaJson : settings?.activeOutputSchemaJson;
export const effectiveRuntimeBindings = (settings: SchemaSettings | null | undefined) => settings?.payloadMode === 'Direct'
  ? settings.effectiveRuntimeBindingsJson : settings?.activeRuntimeBindingsJson;